import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { getNewsImageUrl } from '../../utils/formatters';
import { currentLocale } from '../../i18n';
import { NEWS_CATEGORIES, NEWS_CATEGORY_MAP } from './newsConstants';
import './NewsPage.css';

/**
 * Format an ISO timestamp as a short localised date (e.g. "7 jun 2026").
 *
 * @param {string} iso - ISO datetime string from the API.
 * @returns {string} Localised date, or '' when missing.
 */
function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(currentLocale(), {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch {
    return '';
  }
}

/**
 * Public news hub — a Steam-style card feed with Riot-style category filter
 * tabs. On-site posts open their detail page; external posts open their target
 * URL in a new tab. Filtering is client-side over the published list.
 *
 * @returns {JSX.Element} The news hub page.
 */
export default function NewsPage() {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/news`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (!cancelled) {
          setPosts(Array.isArray(data) ? data : []);
          setLoading(false);
        }
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const visible = active === 'all' ? posts : posts.filter((p) => p.category === active);

  // Only show filter tabs for categories that actually have posts.
  const presentCategories = NEWS_CATEGORIES.filter(
    (c) => posts.some((p) => p.category === c.value)
  );

  /** Open a post: external link in a new tab, otherwise its detail page. */
  const openPost = (post) => {
    if (post.post_type === 'external' && post.external_url) {
      window.open(post.external_url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(`/news/${post.slug}`);
    }
  };

  return (
    <div className="news-page">
      <header className="news-page-header">
        <h1>{t('nav.news')}</h1>
        <p>{t('news.subtitle')}</p>
      </header>

      {presentCategories.length > 0 && (
        <div className="news-filter-tabs">
          <button
            type="button"
            className={`news-tab ${active === 'all' ? 'active' : ''}`}
            onClick={() => setActive('all')}
          >
            {t('matches.all')}
          </button>
          {presentCategories.map((c) => (
            <button
              key={c.value}
              type="button"
              className={`news-tab ${active === c.value ? 'active' : ''}`}
              onClick={() => setActive(c.value)}
            >
              <span className="news-tab-emoji">{c.emoji}</span> {t(c.labelKey)}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="news-loading"><div className="loader" /></div>
      ) : visible.length === 0 ? (
        <p className="news-empty">{t('news.empty')}</p>
      ) : (
        <div className="news-grid">
          {visible.map((post) => {
            const cat = NEWS_CATEGORY_MAP[post.category];
            const cover = getNewsImageUrl(post.cover_image_url);
            return (
              <button
                key={post.id}
                type="button"
                className="news-card"
                onClick={() => openPost(post)}
              >
                <div className="news-card-media">
                  {cover ? (
                    <img src={cover} alt="" loading="lazy" />
                  ) : (
                    <div className="news-card-media-fallback">{cat?.emoji || '📰'}</div>
                  )}
                  {cat && <span className="news-card-badge">{cat.emoji} {t(cat.labelKey)}</span>}
                  {post.post_type === 'external' && (
                    <span className="news-card-external" title={t('news.postTypeExternal')}>🔗</span>
                  )}
                </div>
                <div className="news-card-body">
                  <h2 className="news-card-title">{post.title}</h2>
                  {post.excerpt && <p className="news-card-excerpt">{post.excerpt}</p>}
                  <span className="news-card-date">{formatDate(post.published_at || post.created_at)}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
